/**
 * The Basketball IQ — Single Term Deep-Dive.
 * Definition · When to Use · When NOT to Use · Read Progression · Counters · Scenarios · Related.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, XCircle, Target, TrendingUp, Shuffle, BookOpen, Video, Volume2, Pause } from 'lucide-react';
import api from '../services/api';
import { getSportMeta } from '../lib/sportMeta';
import CoachBSpeak from '../components/CoachBSpeak';

// ─── Read-Aloud (Coach B's ElevenLabs voice + browser SpeechSynthesis fallback) ─
// Uses the ElevenLabs-powered `<CoachBSpeak>` component so students hear the
// term explained in Coach B's own cloned voice. If ElevenLabs is unreachable,
// the component internally falls back to the browser's SpeechSynthesis.
function composeReadAloud(term) {
  const parts = [`${term.term}.`, term.one_line];
  if (term.when_to_use?.length) parts.push('When to use:', term.when_to_use.join('. '));
  if (term.when_NOT_to_use?.length) parts.push('When not to use:', term.when_NOT_to_use.join('. '));
  if (term.read_progression?.length) parts.push('The read progression:', term.read_progression.join('. '));
  if (term.coach_notes) parts.push("Coach's note:", term.coach_notes);
  return parts.join(' ');
}

const ReadAloudButton = ({ term, accent }) => (
  <CoachBSpeak
    text={composeReadAloud(term).slice(0, 480)}
    label="Hear Coach B"
    variant="full"
    accent={accent}
    testId="read-aloud"
  />
);

const GOLD = '#D4AF37';
const GREEN = '#22C55E';
const RED = '#EF4444';

const BasketballIQTerm = ({ sport = 'basketball' }) => {
  const meta = getSportMeta(sport);
  const ACCENT = meta.accent;
  const { slug } = useParams();
  const [term, setTerm] = useState(null);
  const [related, setRelated] = useState([]);
  const [attribution, setAttribution] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/academy/${sport}-iq/term/${slug}`)
      .then((r) => {
        setTerm(r.data.term);
        setRelated(r.data.term?.related || []);
        setAttribution(r.data.attribution || '');
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [sport, slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#050508] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
    </div>
  );

  if (notFound || !term) return (
    <div className="min-h-screen bg-[#050508] text-white pt-20 pb-24 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-black uppercase text-white mb-2">Term not found</h2>
        <Link to={`/${sport}-iq/glossary`} className="text-[11px] font-black uppercase tracking-widest" style={{ color: ACCENT }}>← Back to glossary</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050508] text-white pt-16 pb-24" data-testid={`${sport}-iq-term`}>
      {/* Header */}
      <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: `linear-gradient(to bottom, ${ACCENT}0A, transparent)` }}>
        <div className="container mx-auto max-w-4xl">
          <Link to={`/${sport}-iq/glossary`} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-glossary">
            <ArrowLeft className="w-3 h-3" /> Glossary
          </Link>
          <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
            <Target className="w-3 h-3" /> {term.category?.replace(/-/g, ' ')}
            {term.difficulty && term.difficulty !== 'foundational' && (
              <span className="text-zinc-600">· {term.difficulty}</span>
            )}
          </div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02]" data-testid="term-title">
            {term.term}
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-4 max-w-3xl leading-relaxed">
            {term.one_line}
          </p>
          <div className="mt-5">
            <ReadAloudButton term={term} accent={ACCENT} />
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6 py-10 space-y-6">

        {/* When to Use / When NOT to Use — side by side */}
        <div className="grid md:grid-cols-2 gap-4">
          {term.when_to_use?.length > 0 && (
            <div className="border-2 p-5" style={{ borderColor: `${GREEN}44`, backgroundColor: `${GREEN}0A` }} data-testid="when-to-use">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} />
                <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: GREEN }}>When to Use</span>
              </div>
              <ul className="space-y-2 text-sm text-zinc-200">
                {term.when_to_use.map((item, i) => (
                  <li key={i} className="flex gap-2 leading-relaxed">
                    <span className="text-zinc-500 flex-shrink-0">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {term.when_NOT_to_use?.length > 0 && (
            <div className="border-2 p-5" style={{ borderColor: `${RED}44`, backgroundColor: `${RED}0A` }} data-testid="when-not-to-use">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-4 h-4" style={{ color: RED }} />
                <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: RED }}>When NOT to Use</span>
              </div>
              <ul className="space-y-2 text-sm text-zinc-200">
                {term.when_NOT_to_use.map((item, i) => (
                  <li key={i} className="flex gap-2 leading-relaxed">
                    <span className="text-zinc-500 flex-shrink-0">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Read Progression */}
        {term.read_progression?.length > 0 && (
          <div className="border-2 border-zinc-800 bg-zinc-950 p-6" data-testid="read-progression">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4" style={{ color: GOLD }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: GOLD }}>Read Progression</span>
            </div>
            <ol className="space-y-2.5">
              {term.read_progression.map((step, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 border-2 flex items-center justify-center text-[11px] font-black" style={{ borderColor: GOLD, color: GOLD }}>
                    {i + 1}
                  </span>
                  <span className="text-sm text-zinc-200 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Counters / Counter-actions */}
        {term.counter_actions?.length > 0 && (
          <div className="border-2 border-zinc-800 bg-zinc-950 p-6" data-testid="counter-actions">
            <div className="flex items-center gap-2 mb-3">
              <Shuffle className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>Counter Actions</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {term.counter_actions.map((c, i) => (
                <span key={i} className="border-2 border-zinc-700 px-3 py-1.5 text-[11px] font-bold text-zinc-200">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Example Scenarios */}
        {term.example_scenarios?.length > 0 && (
          <div className="border-2 border-zinc-800 bg-zinc-950 p-6" data-testid="example-scenarios">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>Example Reads</span>
            </div>
            <div className="space-y-4">
              {term.example_scenarios.map((s, i) => (
                <div key={i} className="border-l-2 pl-4" style={{ borderColor: ACCENT }}>
                  <div className="text-[9px] tracking-[0.3em] uppercase font-black text-zinc-500 mb-1">Scenario {i + 1}</div>
                  <div className="text-sm text-zinc-300 leading-relaxed"><span className="text-zinc-500">Context:</span> {s.context}</div>
                  <div className="text-sm mt-1 leading-relaxed" style={{ color: GREEN }}>
                    <span className="text-zinc-500">→ Read:</span> {s.correct_choice}
                  </div>
                  {s.why && <div className="text-xs text-zinc-400 mt-1 italic">{s.why}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coach's Notes */}
        {term.coach_notes && (
          <div className="border-2 p-6" style={{ borderColor: `${GOLD}44`, backgroundColor: `${GOLD}0A` }} data-testid="coach-notes">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4" style={{ color: GOLD }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: GOLD }}>Coach's Note</span>
            </div>
            <blockquote className="text-white text-base font-bold italic leading-relaxed">
              "{term.coach_notes}"
            </blockquote>
            <div className="text-[10px] tracking-[0.3em] uppercase font-black text-zinc-500 mt-3">— Coach B</div>
          </div>
        )}

        {/* Video (Sora clip if generated) */}
        {term.video_url && (
          <div className="border-2 border-zinc-800 bg-zinc-950 p-4" data-testid="term-video">
            <div className="flex items-center gap-2 mb-2">
              <Video className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>See it Run</span>
            </div>
            <video src={term.video_url} controls className="w-full" />
          </div>
        )}

        {/* Related terms */}
        {related.length > 0 && (
          <div className="pt-6 border-t border-zinc-900" data-testid="related-terms">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-4">Related Reads</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/${sport}-iq/term/${r.slug}`} className="group border border-zinc-800 bg-zinc-950 p-4 hover:border-orange-500 transition-all">
                  <div className="text-[9px] tracking-[0.3em] uppercase font-black text-zinc-500">{r.category?.replace(/-/g, ' ')}</div>
                  <div className="font-black text-sm text-white mt-1 flex items-center justify-between">
                    {r.term}
                    <ArrowRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" />
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{r.one_line}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Attribution */}
        <div className="pt-6 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-600">{attribution}</div>
        </div>
      </div>
    </div>
  );
};

export default BasketballIQTerm;
